/**
 * Item Library Integration for Foundations
 * Maps field names to rates from Item Library API
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { FOUNDATIONS_REFERENCES, getMeshReinforcementCode, applyBlockTypeOverrides, CONCRETE_STRIP_CODE_MAP, CONCRETE_FIELD_PREFIX_CODES } from './foundationsReferenceMapping';
import { FOUNDATIONS_FIELD_UNITS } from './foundationsFieldMapping';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
  // Raw library rate before any m³→m² thickness conversion is applied.
  // For combined codes (e.g. ALP031A+GWALP004) this is the sum of both
  // component nettRates — no thickness factor baked in.
  rawRate?: number;
}

/**
 * Fields that use m³ rates but are quantified per m² in the bill summary.
 * Maps field name → function that extracts the thickness (in m) from groundworksData.
 * Effective m² rate = m³ rate × thickness (m).
 */
const M3_RATE_THICKNESS_SOURCES: Record<string, (data: any) => string | undefined> = {
  // GWALP004 – 6F2 piling mat; thickness stored in foundationsPrefixValues.pileMat
  pileMat: (data: any) => data?.foundationsPrefixValues?.pileMat,
  // GWALP013a – concrete blinding; thickness stored in foundationsPrefixValues.concreteBlinding
  concreteBlinding: (data: any) => data?.foundationsPrefixValues?.concreteBlinding,
};
export const FOUNDATIONS_M3_CONVERSION_FIELDS: ReadonlySet<string> = new Set(Object.keys(M3_RATE_THICKNESS_SOURCES));

function parseThicknessValue(raw: string): number | null {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

function getM3ToM2Multiplier(fieldName: string, groundworksData: any): number {
  const getThickness = M3_RATE_THICKNESS_SOURCES[fieldName];
  if (!getThickness) return 1;
  const raw = getThickness(groundworksData);
  if (!raw) return 1;
  const value = parseThicknessValue(raw);
  if (!value || value <= 0) return 1;
  // value >= 1 → entered as mm (e.g. "900") → convert to metres
  // value < 1  → already metres (e.g. "0.900")
  return value >= 1 ? value / 1000 : value;
}

/**
 * Dynamic field handlers
 * These fields get their codes from groundworks data selections
 */

interface DynamicFieldHandler {
  getCode: (groundworksData: any) => string | undefined;
  getDescription?: (groundworksData: any) => string | undefined;
}

const DYNAMIC_FIELD_HANDLERS: Record<string, DynamicFieldHandler> = {
  meshReinforcement: {
    getCode: (groundworksData: any) => {
      const meshType = groundworksData?.meshReinforcementType;
      return getMeshReinforcementCode(meshType);
    },
    getDescription: (groundworksData: any) => {
      const meshType = groundworksData?.meshReinforcementType;
      return meshType ? `Mesh Reinforcement ${meshType}` : undefined;
    },
  },
  bbFloorHouse: {
    getCode: (groundworksData: any) => {
      return groundworksData?.foundationsPrefixValues?.bbFloorHouse;
    },
  },
  // bbFloorGarage: the Configure modal Floors→Garage selector stores the chosen
  // GWALPF code (GWALPF006 / GWALPF012) in foundationsPrefixValues. When unset
  // there is no static fallback — the reference in foundationsReferenceMapping
  // is intentionally empty so the field resolves to rate 0 / no resource code
  // until the estimator picks an option.
  bbFloorGarage: {
    getCode: (groundworksData: any) => {
      return groundworksData?.foundationsPrefixValues?.bbFloorGarage;
    },
  },
  meshToFloor: {
    getCode: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.meshToFloor;
      return getMeshReinforcementCode(meshType);
    },
    getDescription: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.meshToFloor;
      return meshType ? `Mesh to Floor ${meshType}` : undefined;
    },
  },
  topLayerMesh: {
    getCode: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.topLayerMesh;
      return getMeshReinforcementCode(meshType);
    },
    getDescription: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.topLayerMesh;
      return meshType ? `Top Layer Mesh ${meshType}` : undefined;
    },
  },
  meshToGarageFloor: {
    getCode: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.meshToGarageFloor;
      return getMeshReinforcementCode(meshType);
    },
    getDescription: (groundworksData: any) => {
      const meshType = groundworksData?.foundationsPrefixValues?.meshToGarageFloor;
      return meshType ? `Mesh to Garage Floor ${meshType}` : undefined;
    },
  },
  // Terram on Piling Mat: when enabled, the pileMat resource code becomes
  // the combined "ALP031A+GWALP004" so getRateByCode sums both nettRates.
  // Returning undefined falls through to the static GWALP004 reference code.
  pileMat: {
    getCode: (groundworksData: any) => {
      return groundworksData?.foundationsPrefixValues?.terram === 'true'
        ? 'ALP031A+GWALP004'
        : undefined;
    },
  },
  // Concrete-type fields. The selected concrete prefix (GEN1, RC28/35, …) maps
  // to its RMC code via CONCRETE_STRIP_CODE_MAP, then combines with the field's
  // placement prefix from CONCRETE_FIELD_PREFIX_CODES (gen1ConcreteToStrip →
  // GWALP013, rc35ConcreteToPiled → GWALP019a, rc35ConcreteToVibro → GWALP025)
  // so getRateByCode sums the placement rate and the ready-mix concrete rate
  // (e.g. GWALP013+RMC001 for GEN1 strip, GWALP019a+RMC0004 for RC28/35 piled).
  gen1ConcreteToStrip: {
    getCode: (groundworksData: any) => buildConcreteFieldCode('gen1ConcreteToStrip', groundworksData),
  },
  rc35ConcreteToPiled: {
    getCode: (groundworksData: any) => buildConcreteFieldCode('rc35ConcreteToPiled', groundworksData),
  },
  rc35ConcreteToVibro: {
    getCode: (groundworksData: any) => buildConcreteFieldCode('rc35ConcreteToVibro', groundworksData),
  },
  // Abnormals tab — shares the GWALP013 placement prefix with `gen1ConcreteToStrip`.
  gen1AdditionalConcrete: {
    getCode: (groundworksData: any) => buildConcreteFieldCode('gen1AdditionalConcrete', groundworksData),
  },
};

function buildConcreteFieldCode(fieldName: string, groundworksData: any): string | undefined {
  const concreteType = groundworksData?.foundationsPrefixValues?.[fieldName];
  const rmc = concreteType ? CONCRETE_STRIP_CODE_MAP[concreteType] : undefined;
  if (!rmc) return undefined;
  const prefix = CONCRETE_FIELD_PREFIX_CODES[fieldName];
  return prefix ? `${prefix}+${rmc}` : rmc;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();

  // Combined codes (e.g. "ALP031A+GWALP004"): sum component nettRates.
  // Description / unit are taken from the first matched component; combined code
  // is preserved as the resourceCode so downstream consumers see the full string.
  if (normalizedCode.includes('+')) {
    // Historical version: `buildItemLibraryFromBoqSnapshot` (useItemLibrary.ts)
    // reconstructs the item library from the BOQ snapshot's `groundworksItemLibrary`
    // map, where a combined-code field (e.g. rc35ConcreteToPiled →
    // "GWALP019a+RMC0004") was stored as ONE entry keyed by that full combined
    // string, with `nettRate` already the pre-summed value from save time — the
    // live item library never has a real resourceCode literally containing "+",
    // so this exact-match lookup is a no-op for the live/current version and only
    // ever resolves here for a historical snapshot. Without it, the split-and-sum
    // logic below looks up each component code individually (e.g. "GWALP019A",
    // "RMC0004") — codes that don't exist as their own entries in a
    // snapshot-reconstructed map — and silently prices the field at 0.
    const combinedItem = resourceCodeMap.get(normalizedCode);
    if (combinedItem) {
      return {
        rate: combinedItem.nettRate || combinedItem.baseRate || 0,
        description: combinedItem.resourceDescription || fallbackDescription || fieldName,
        unit: combinedItem.unit || 'Nr',
        resourceCode: normalizedCode,
      };
    }
    const parts = normalizedCode.split('+').map((p) => p.trim()).filter(Boolean);
    let summedRate = 0;
    let firstItem: ItemLibraryItem | undefined;
    for (const part of parts) {
      const item = resourceCodeMap.get(part);
      summedRate += item?.nettRate || item?.baseRate || 0;
      if (!firstItem && item) firstItem = item;
    }
    return {
      rate: summedRate,
      description: firstItem?.resourceDescription || fallbackDescription || fieldName,
      unit: firstItem?.unit || 'Nr',
      resourceCode: normalizedCode,
    };
  }

  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'Nr',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - preserve the normalized code so dev mode can show what code was expected
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'Nr',
    resourceCode: normalizedCode,
  };
}

/**
 * Create a rate lookup map from item library for Foundations
 * @param itemLibraryData - Item library response from API
 * @param groundworksData - Groundworks data for dynamic field values (optional)
 */
export function createFoundationsRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksData?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Apply block-type-specific code overrides (e.g. Celcon - general uses different GWALPS codes)
  const effectiveRefs = applyBlockTypeOverrides(FOUNDATIONS_REFERENCES, groundworksData?.substructureBlockType);

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(effectiveRefs).forEach(([fieldName, reference]) => {
    // Check if this is a dynamic field
    const dynamicHandler = DYNAMIC_FIELD_HANDLERS[fieldName];

    if (dynamicHandler && groundworksData) {
      // Dynamic field - get code and fallback description from handler
      const dynamicCode = dynamicHandler.getCode(groundworksData);
      const fallbackDescription = dynamicHandler.getDescription?.(groundworksData);

      if (dynamicCode) {
        const rateInfo = getRateByCode(dynamicCode, resourceCodeMap, fieldName, fallbackDescription);
        const m3ToM2 = getM3ToM2Multiplier(fieldName, groundworksData);

        let finalRate: number;
        let rawRate: number;
        // pileMat + Terram: only the 6F2 piling mat (GWALP004) is a volumetric
        // material — apply m3→m2 thickness conversion to it only. Terram (ALP031A)
        // is a flat m2 rate and must be added AFTER the conversion, not multiplied
        // by the thickness alongside the piling mat rate.
        // rawRate is the sum of both component API rates with NO thickness applied.
        if (fieldName === 'pileMat' && dynamicCode.includes('ALP031A')) {
          const pilingMatItem = resourceCodeMap.get('GWALP004');
          const terramItem = resourceCodeMap.get('ALP031A');
          if (pilingMatItem || terramItem) {
            const pilingMatRaw = pilingMatItem?.nettRate || pilingMatItem?.baseRate || 0;
            const terramRaw = terramItem?.nettRate || terramItem?.baseRate || 0;
            finalRate = pilingMatRaw * m3ToM2 + terramRaw;
            rawRate = pilingMatRaw + terramRaw;
          } else {
            // Historical version: neither component code exists on its own in a
            // snapshot-reconstructed item library (see getRateByCode's exact-match
            // branch above) — only the pre-summed combined rate survived, with no
            // way to split piling-mat vs Terram back out for the m³→m² conversion.
            // Best-effort fallback: use the already-summed rate as-is.
            finalRate = rateInfo.rate;
            rawRate = rateInfo.rate;
          }
        } else {
          finalRate = rateInfo.rate * m3ToM2;
          rawRate = rateInfo.rate;
        }

        rateMap.set(fieldName, {
          ...rateInfo,
          rate: finalRate,
          rawRate,
          unit: fieldName in FOUNDATIONS_FIELD_UNITS ? FOUNDATIONS_FIELD_UNITS[fieldName] : rateInfo.unit,
        });
        return;
      }

      // If no dynamic code found but we have a fallback description, use it
      if (fallbackDescription) {
        rateMap.set(fieldName, {
          rate: 0,
          description: fallbackDescription,
          unit: FOUNDATIONS_FIELD_UNITS[fieldName] ?? '',
        });
        return;
      }
      // Otherwise fall through to check static code
    }

    if (!reference.code) {
      // Fields without static or dynamic code
      rateMap.set(fieldName, {
        rate: 0,
        description: fieldName,
        unit: FOUNDATIONS_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName);
    const m3ToM2 = getM3ToM2Multiplier(fieldName, groundworksData);
    rateMap.set(fieldName, {
      ...rateInfo,
      rate: rateInfo.rate * m3ToM2,
      rawRate: rateInfo.rate,
      unit: fieldName in FOUNDATIONS_FIELD_UNITS ? FOUNDATIONS_FIELD_UNITS[fieldName] : rateInfo.unit,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getFoundationsRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: FOUNDATIONS_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Foundations summary items
 */
export function applyFoundationsRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getFoundationsRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
